/**
 * @fileoverview Project API-1657 boundary payloads into slug → SVG path maps.
 *
 * Companion to `footprintPath.ts` (pure converter) and
 * `useNeighborhoodFootprints` (fetch). Mirrors `centroids.ts` /
 * `toNeighborhoodCentroids` so hero art and row hoodmarks share one source.
 */

import type { GeoJsonBbox, NeighborhoodListItem } from '@loci/validation';

import { boundaryToFootprintPath } from '@/lib/neighborhoods/footprintPath';

/**
 * Build a slug → normalised SVG path lookup from list-endpoint items that
 * carry `boundary` + `bbox`. Items without a drawable footprint are omitted
 * (honest empty — callers treat missing slugs as `unknown-slug`).
 */
export function toNeighborhoodFootprintPaths(
  items: readonly NeighborhoodListItem[],
): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  for (const item of items) {
    const path = boundaryToFootprintPath(item.boundary, item.bbox);
    if (!path) continue;
    out.set(item.slug, path);
  }
  return out;
}

/** One neighborhood outline, projected into a city-wide unit viewBox. */
export interface CityFootprint {
  readonly slug: string;
  /** Unit-viewBox SVG path `d`, sharing {@link CityFootprintProjection.bbox}. */
  readonly path: string;
}

/**
 * Every drawable footprint for a city, in one shared coordinate frame.
 *
 * Distinct from {@link toNeighborhoodFootprintPaths}, which fits each outline
 * to its OWN bbox so a 44px hoodmark fills its box. Here one union extent is
 * used for all of them, so the outlines land in their true relative positions
 * and read as the city rather than a pile of overlapping glyphs.
 */
export interface CityFootprintProjection {
  /** Union of every drawn footprint's bbox: `[minLon, minLat, maxLon, maxLat]`. */
  readonly bbox: GeoJsonBbox;
  readonly footprints: readonly CityFootprint[];
}

/**
 * Project list-endpoint items into a single city-wide unit viewBox (WEB-654).
 *
 * Same payload, same {@link boundaryToFootprintPath} converter and same
 * simplified geometry the row hoodmarks draw — only the bbox differs, so the
 * hero and the list rhyme instead of coming from two geometry sources.
 *
 * @returns `null` when nothing is drawable, so callers render honest absence
 *   rather than a placeholder shape.
 */
export function toCityFootprintProjection(
  items: readonly NeighborhoodListItem[],
): CityFootprintProjection | null {
  const drawable = items.filter((item) => item.boundary && item.bbox);
  if (drawable.length === 0) return null;

  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  for (const item of drawable) {
    // `drawable` guarantees bbox is present; the guard keeps TS honest.
    if (!item.bbox) continue;
    const [itemMinLon, itemMinLat, itemMaxLon, itemMaxLat] = item.bbox;
    if (
      !Number.isFinite(itemMinLon) ||
      !Number.isFinite(itemMinLat) ||
      !Number.isFinite(itemMaxLon) ||
      !Number.isFinite(itemMaxLat)
    ) {
      continue;
    }
    minLon = Math.min(minLon, itemMinLon);
    minLat = Math.min(minLat, itemMinLat);
    maxLon = Math.max(maxLon, itemMaxLon);
    maxLat = Math.max(maxLat, itemMaxLat);
  }

  if (!(maxLon > minLon) || !(maxLat > minLat)) return null;

  const bbox: GeoJsonBbox = [minLon, minLat, maxLon, maxLat];
  const footprints: CityFootprint[] = [];
  for (const item of drawable) {
    const path = boundaryToFootprintPath(item.boundary, bbox);
    if (!path) continue;
    footprints.push({ slug: item.slug, path });
  }

  return footprints.length > 0 ? { bbox, footprints } : null;
}
